export interface IndianAddressComponents {
  building?: string;
  streetName?: string;
  locality?: string;
  subLocality?: string;
  city?: string;
  district?: string;
  state?: string;
  pincode?: string;
  formattedAddress?: string;
  confidence?: number;
}

export interface GeocodeResult {
  success: boolean;
  addressComponents?: IndianAddressComponents;
  rawResponse?: any;
  error?: string;
}

export interface OutletAddressFields {
  building: string;
  streetName: string;
  landmark: string;
  subLocality: string;
  suggestedAreaName: string;
  suggestedLocalityName: string;
  pincode: string;
  confidence: number;
}

export interface ParsedAddressResult {
  success: boolean;
  addressFields?: OutletAddressFields;
  rawResponse?: any;
  error?: string;
  cityValidation?: {
    detectedCity: string;
    isAllowed: boolean;
    allowedCity: string;
    message: string;
  };
}

class GeocodingService {
  private geocoder: any = null;

  constructor() {
    // Don't initialize here - do it lazily when needed
  }

  private async ensureGeocoderInitialized(): Promise<boolean> {
    if (this.geocoder) {
      return true;
    }

    // Wait for Google Maps to be fully available
    let retries = 0;
    while (retries < 10) { // Reduced from 25 to 10
      const google = (window as any).google;
      
      // Check if Google Maps is still loading
      if ((window as any).googleMapsLoading) {
        await new Promise(resolve => setTimeout(resolve, 200)); // Reduced from 300ms
        retries++;
        continue;
      }
      
      if (google?.maps?.Geocoder && typeof google.maps.Geocoder === 'function') {
        try {
          this.geocoder = new google.maps.Geocoder();
          console.log('✅ Geocoder initialized');
          return true;
        } catch (error) {
          console.error('❌ Error creating geocoder:', error);
          return false;
        }
      }
      
      await new Promise(resolve => setTimeout(resolve, 200)); // Reduced from 300ms
      retries++;
    }

    console.error('❌ Failed to initialize geocoder');
    return false;
  }

  public async reverseGeocode(lat: number, lng: number): Promise<GeocodeResult> {
    // Ensure geocoder is initialized before using it
    const initialized = await this.ensureGeocoderInitialized();
    if (!initialized) {
      return {
        success: false,
        error: 'Google Maps Geocoder not initialized - Google Maps API may not be loaded'
      };
    }

    try {
      const response = await new Promise<any>((resolve, reject) => {
        this.geocoder!.geocode(
          {
            location: { lat, lng },
            region: 'IN', // Bias results to India
            language: 'en' // Force English language responses to avoid Hindi/local language issues
          },
          (results: any, status: any) => {
            if (status === 'OK' && results) {
              console.log(`✅ Geocoding successful - ${results.length} results`);
              resolve({ results, status });
            } else {
              console.error(`❌ Geocoding failed: ${status}`);
              reject(new Error(`Geocoding failed: ${status}`));
            }
          }
        );
      });

      if (response.results && response.results.length > 0) {
        const addressComponents = this.parseIndianAddress(response.results[0]);
        console.log('📍 Parsed address components:', addressComponents);
        
        // Log the formatted address to verify English language response
        console.log('🌍 Formatted address (should be in English):', response.results[0].formatted_address);
        
        return {
          success: true,
          addressComponents,
          rawResponse: response.results[0]
        };
      } else {
        console.error('❌ No results found in geocoding response');
        return {
          success: false,
          error: 'No results found for the given coordinates'
        };
      }
    } catch (error) {
      console.error('❌ Geocoding error caught:', error);
      console.error('🔍 Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown geocoding error'
      };
    }
  }

  private parseIndianAddress(result: any): IndianAddressComponents {
    // Prefer ASCII/Latin names when available to ensure consistency
    const normalizeName = (longName?: string, shortName?: string): string => {
      const isAscii = (s?: string) => !!s && !/[^\x00-\x7F]/.test(s);
      if (isAscii(longName)) return longName as string;
      if (isAscii(shortName)) return shortName as string;
      return longName || shortName || '';
    };

    const components: IndianAddressComponents = {
      formattedAddress: result.formatted_address,
      confidence: 0.8 // Default confidence
    };

    // Parse address components
    result.address_components.forEach((component: any) => {
      const types = component.types;

      if (types.includes('street_number')) {
        components.building = normalizeName(component.long_name, component.short_name);
      } else if (types.includes('route')) {
        components.streetName = normalizeName(component.long_name, component.short_name);
      } else if (types.includes('sublocality_level_2') || types.includes('sublocality_level_1')) {
        if (!components.locality) {
          components.locality = normalizeName(component.long_name, component.short_name);
        } else {
          components.subLocality = normalizeName(component.long_name, component.short_name);
        }
      } else if (types.includes('locality')) {
        components.city = normalizeName(component.long_name, component.short_name);
      } else if (types.includes('administrative_area_level_2')) {
        components.district = normalizeName(component.long_name, component.short_name);
      } else if (types.includes('administrative_area_level_1')) {
        components.state = normalizeName(component.long_name, component.short_name);
      } else if (types.includes('postal_code')) {
        components.pincode = component.long_name;
      }
    });

    // Handle Mumbai-specific parsing
    if (components.city?.toLowerCase().includes('mumbai')) {
      // Extract area information from sublocality or locality
      if (components.locality && !components.subLocality) {
        components.subLocality = components.locality;
      }
    }

    return components;
  }

  public async parseForOutletFields(lat: number, lng: number, allowedCity?: string): Promise<ParsedAddressResult> {
    // First get the geocoding result
    const geocodeResult = await this.reverseGeocode(lat, lng);
    
    if (!geocodeResult.success || !geocodeResult.rawResponse) {
      console.error('❌ Address parsing failed:', geocodeResult.error);
      return {
        success: false,
        error: geocodeResult.error || 'Failed to geocode location'
      };
    }

    try {
      const result = geocodeResult.rawResponse;
      const addressFields = this.parseGoogleResponseToOutletFields(result);

      // Fail fast during testing if area/locality missing
      if (!addressFields.suggestedAreaName || !addressFields.suggestedLocalityName) {
        const missing = [
          !addressFields.suggestedAreaName ? 'area (sublocality_level_1)' : null,
          !addressFields.suggestedLocalityName ? 'locality (SL2→SL3→neighborhood→route)' : null
        ].filter(Boolean).join(' & ');
        return {
          success: false,
          error: `Missing required fields: ${missing}`,
          rawResponse: result
        };
      }
      
      // Extract detected city for validation
      const detectedCity = this.extractCityFromResult(result);
      
      // Validate city if allowedCity is provided
      let cityValidation;
      if (allowedCity && detectedCity) {
        const isAllowed = this.isCityAllowed(detectedCity, allowedCity);
        cityValidation = {
          detectedCity,
          isAllowed,
          allowedCity,
          message: isAllowed 
            ? `✅ Location within ${allowedCity}`
            : `🚫 Location in "${detectedCity}" but only "${allowedCity}" allowed`
        };
        
        if (!isAllowed) {
          console.warn('⚠️ City validation failed:', cityValidation.message);
          return {
            success: false,
            error: `Location outside allowed city. Detected: ${detectedCity}, Allowed: ${allowedCity}`,
            cityValidation,
            rawResponse: result
          };
        }
      }
      
      console.log('✅ Address parsing successful:', { 
        area: addressFields.suggestedAreaName, 
        locality: addressFields.suggestedLocalityName 
      });
      return {
        success: true,
        addressFields,
        rawResponse: result,
        cityValidation
      };
    } catch (error) {
      console.error('❌ Error parsing address:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to parse address components'
      };
    }
  }

  private extractCityFromResult(result: any): string {
    // Extract city from address components
    for (const component of result.address_components) {
      if (component.types.includes('locality')) {
        return component.long_name;
      }
    }
    
    // Fallback: try administrative_area_level_2 (district)
    for (const component of result.address_components) {
      if (component.types.includes('administrative_area_level_2')) {
        return component.long_name;
      }
    }
    
    return '';
  }

  private isCityAllowed(detectedCity: string, allowedCity: string): boolean {
    // Common Hindi-English city name mappings (fallback in case language parameter fails)
    const cityTranslations: { [key: string]: string } = {
      'मुंबई': 'mumbai',
      'पुणे': 'pune',
      'दिल्ली': 'delhi',
      'बैंगलोर': 'bangalore',
      'चेन्नई': 'chennai',
      'कोलकाता': 'kolkata',
      'हैदराबाद': 'hyderabad',
      'अहमदाबाद': 'ahmedabad',
      'जयपुर': 'jaipur',
      'सूरत': 'surat'
    };
    
    // Normalize city names for comparison (case insensitive, remove common variations)
    const normalize = (city: string) => {
      let normalized = city.toLowerCase()
        .replace(/\s+/g, '')
        .replace(/mumbai|bombay/g, 'mumbai')
        .replace(/bengaluru|bangalore/g, 'bangalore')
        .replace(/chennai|madras/g, 'chennai')
        .replace(/kolkata|calcutta/g, 'kolkata');
        
      // Apply Hindi-English mapping if needed
      const translated = cityTranslations[city];
      if (translated) {
        normalized = translated;
      }
      
      return normalized;
    };
    
    const normalizedDetected = normalize(detectedCity);
    const normalizedAllowed = normalize(allowedCity);
    
    console.log('🏙️ City validation:', {
      original: detectedCity,
      normalized: normalizedDetected,
      allowedOriginal: allowedCity,
      allowedNormalized: normalizedAllowed
    });
    
    // Check exact match or if one contains the other
    return normalizedDetected === normalizedAllowed ||
           normalizedDetected.includes(normalizedAllowed) ||
           normalizedAllowed.includes(normalizedDetected);
  }

  private parseGoogleResponseToOutletFields(result: any): OutletAddressFields {
    // Prefer ASCII/Latin names when available to ensure consistency
    const normalizeName = (longName?: string, shortName?: string): string => {
      const isAscii = (s?: string) => !!s && !/[^\x00-\x7F]/.test(s);
      if (isAscii(longName)) return longName as string;
      if (isAscii(shortName)) return shortName as string;
      return longName || shortName || '';
    };

    let building = '';
    let streetParts: string[] = [];
    let landmark = '';
    let subLocality = '';
    let areaName = '';
    let localityName = '';
    let pincode = '';
    
    // Collect potential landmarks and sub-localities
    let potentialLandmarks: string[] = [];
    let potentialSubLocalities: string[] = [];
    
    // Track raw component picks for deterministic selection
    let raw_sl1: string | undefined;
    let raw_sl2: string | undefined;
    let raw_sl3: string | undefined;
    let raw_neighborhood: string | undefined;
    let raw_route: string | undefined;

    // Parse address components based on your mapping table
    result.address_components.forEach((component: any) => {
      const types = component.types;
      const longName = normalizeName(component.long_name, component.short_name);

      // Building field: Extract from premise or street_number
      if (types.includes('premise') || types.includes('street_number')) {
        if (!building) building = longName;
      }
      
      // Enhanced Landmark field extraction
      if (types.includes('landmark')) {
        potentialLandmarks.push(longName);
      }
      if (types.includes('point_of_interest')) {
        potentialLandmarks.push(longName);
      }
      if (types.includes('establishment')) {
        potentialLandmarks.push(longName);
      }
      // Use premise as potential landmark if not already used for building
      if (types.includes('premise') && building !== longName) {
        potentialLandmarks.push(longName);
      }
      
      // Street Name field: Combine route + landmark + premise
      if (types.includes('route')) {
        streetParts.push(longName);
        if (!raw_route) raw_route = longName;
      }
      
      // Enhanced Sub-Locality field extraction (try all sublocality levels)
      if (types.includes('sublocality_level_2')) {
        potentialSubLocalities.push(longName);
        if (!raw_sl2) raw_sl2 = longName;
      }
      if (types.includes('sublocality_level_2')) {
        potentialSubLocalities.push(longName);
      }
      if (types.includes('sublocality_level_1') && !potentialSubLocalities.length) {
        potentialSubLocalities.push(longName);
      }
      if (types.includes('sublocality_level_1') && !raw_sl1) {
        raw_sl1 = longName;
      }
      // Sometimes sublocality appears without level
      if (types.includes('sublocality') && !potentialSubLocalities.length) {
        potentialSubLocalities.push(longName);
      }
      
      // Area field: Use sublocality_level_1 only (no fallback to SL2)
      if (types.includes('sublocality_level_1')) {
        areaName = longName;
      }

      // Locality will be selected deterministically later; only capture raw markers
      if (types.includes('sublocality_level_3') && !raw_sl3) {
        raw_sl3 = longName;
      }
      // Neighborhood capture (as potential locality fallback)
      if (types.includes('neighborhood') && !raw_neighborhood) {
        raw_neighborhood = longName;
      }
      
      // Pincode field: Direct from postal_code
      if (types.includes('postal_code')) {
        pincode = component.long_name; // Keep pincode as is (numeric)
      }
    });

    // Process collected landmarks
    if (potentialLandmarks.length > 0) {
      // Use the first landmark that's not the same as building
      landmark = potentialLandmarks.find(l => l !== building) || potentialLandmarks[0];
    }

    // Process collected sub-localities
    if (potentialSubLocalities.length > 0) {
      // Use the most specific sub-locality (first one, as we prioritized level_3 > level_2 > level_1)
      subLocality = potentialSubLocalities[0];
    }

    // Extract landmarks from formatted address if still empty
    if (!landmark) {
      const formattedAddress = result.formatted_address || '';
      const landmarkPatterns = [
        /near\s+([^,]+)/i,
        /opposite\s+([^,]+)/i,
        /behind\s+([^,]+)/i,
        /beside\s+([^,]+)/i,
        /next\s+to\s+([^,]+)/i
      ];
      
      for (const pattern of landmarkPatterns) {
        const match = formattedAddress.match(pattern);
        if (match && match[1]) {
          landmark = match[1].trim();
          break;
        }
      }
    }

    // Construct street name: route-only per requirement
    const streetName = raw_route || '';

    // Deterministic locality selection (override previous localityName if applicable)
    // Order: SL2 -> SL3 -> neighborhood -> route
    if (raw_sl2) {
      localityName = raw_sl2;
    } else if (raw_sl3) {
      localityName = raw_sl3;
    } else if (raw_neighborhood) {
      localityName = raw_neighborhood;
    } else if (!localityName && raw_route) {
      localityName = raw_route;
    }

    // Remove formatted_address-based fallbacks for area/locality (fail fast instead)

    // Additional fallback for sub-locality if still empty
    if (!subLocality && localityName && areaName && localityName !== areaName) {
      // If we have both locality and area names, use the more specific one as subLocality
      const formattedParts = result.formatted_address.split(',').map((part: string) => part.trim());
      const streetPart = formattedParts[0] || '';
      // Extract potential sub-locality from the first part of address
      const words = streetPart.split(' ');
      if (words.length > 2) {
        // Take the last few words before comma as potential sub-locality
        subLocality = words.slice(-2).join(' ');
      }
    }

    return {
      building: building || '',
      streetName: streetName || '',
      landmark: landmark || '',
      subLocality: subLocality || '',
      suggestedAreaName: areaName || '',
      suggestedLocalityName: localityName || '',
      pincode: pincode || '',
      confidence: result.geometry?.location_type === 'ROOFTOP' ? 0.9 : 0.7
    };
  }

  public isInitialized(): boolean {
    return this.geocoder !== null;
  }
}

export const geocodingService = new GeocodingService();
